<template>
  <div
    v-if="showWidget"
    class="recommended-today-component-container"
  >
    <dashboard-widget-header
      :title="config.name || 'Recommended for Today'"
      :icon="widgetIcon"
    />
    <div class="recommended-items-container">
      <carousel
        :item-width="351"
        :tile-count="assets.length"
        :tile-type="'block'"
      >
        <asset-block-tile
          v-for="asset in assets"
          :key="asset.key"
          class="recommended-item"
          :asset="asset.asset"
          :asset-state="assetState(asset.asset)"
          :display-attachment="assetDisplayAttachment(asset.asset)"
          @tile-clicked="handleTileClick(asset)"
        />
      </carousel>
    </div>
  </div>
</template>

<script>
import AssetBlockTile from '@/components/singletons/asset-block-tile.vue';
import Carousel from '@/components/carousels/carousel.vue';
import DashboardWidgetHeader from '@components/dashboard-widgets/header.vue';
import DashboardWidgetMixin from '@/mixins/dashboardWidget';

export default {
  name: 'RecommendedTodayWidget',
  components: { AssetBlockTile, Carousel, DashboardWidgetHeader },
  mixins: [DashboardWidgetMixin],
  props: {
    config: {
      type: Object,
    },
    dashboard: {
      type: Object,
    },
  },
  data() {
    return {
      attachmentIds: [],
      loaded: false,
      show: false,
      topicIds: [],
    };
  },
  computed: {
    assets() {
      const baseRoute = { name: 'library#show' };
      const attachmentMap = this.attachments.map(attachment => {
        return {
          asset: attachment,
          key: `attachment-${attachment.id}`,
          route: {...baseRoute, params: { assetType: 'attachment', assetId: attachment.id }},
        };
      });
      const topicMap = this.topics.map(topic => {
        return {
          asset: topic,
          key: `topic-${topic.id}`,
          route: {...baseRoute, params: { assetType: 'topic', assetId: topic.id }},
        };
      });
      return [...attachmentMap, ...topicMap];
    },
    attachments() {
      return this.attachmentIds.map(id => this.$store.getters['attachments/get'][id]).filter(a => a);
    },
    attachmentStates() {
      return this.$store.getters['attachmentStates/get'];
    },
    showWidget() {
      return this.show && this.loaded && this.assets?.length > 0;
    },
    singleItemLeft() {
      const totalAssets = this.assets.length;
      const completedAssets = this.assets.filter(a => a.asset.completedState(this.$store)).length;
      return (totalAssets - completedAssets) === 1;
    },
    topics() {
      return this.topicIds.map(id => this.$store.getters['topics/topics'][id]).filter(topic => topic);
    },
    topicStates() {
      return this.$store.getters['topicStates/topicStates'];
    },
  },
  created() {
    this.$store.getters['session/ajax'].get('/library_user_queue').then(({ data }) => {
      if (data) {
        this.attachmentIds = data.attachmentIds || [];
        this.topicIds = data.topicIds || [];
        if (data.topics?.length > 0) {
          this.$store.commit('topics/setTopics', data.topics);
        }
        if (data.attachments?.length > 0) {
          this.$store.commit('attachments/setAttachments', data.attachments);
        }
      }
    }).finally(() => this.loaded = true);
    this.checkShow();
  },
  methods: {
    analyticsParams(item) {
      let params = {
        dashboardId: this.dashboard.id,
        dashboardTitle: this.dashboard.name,
      };
      if (item) {
        params.assetId = item.id;
        params.assetTitle = item.name || item.title;
        params.assetType = item.assetType;
      }
      return params;
    },
    assetDisplayAttachment(asset) {
      if (asset?.displayAttachmentId) {
        return this.$store.getters['attachments/get'][asset.displayAttachmentId];
      }
    },
    assetState(asset) {
      if (asset.assetType === 'Attachment') {
        return this.attachmentStates[asset.id];
      } else if (asset.assetType === 'Topic') {
        return this.topicStates[asset.id];
      }
    },
    checkShow() {
      const user = this.$store.getters['session/currentUser'];
      if (user?.previewer) {
        this.show = true;
      } else if (this.$apptimize.enabled()) {
        const widgetEnabled = this.$apptimize.checkExperiments({
          experimentName: 'Recommended Today Widget',
          bool: 'recommendedTodayWidgetEnabled',
          defaultVal: false,
          matcher: /recommended today widget/gi,
        });
        this.show = widgetEnabled;
      }
    },
    handleTileClick(item) {
      const params = this.analyticsParams(item.asset);
      this.$analytics.events.viewedRecommendedItemFromWidget(params);
      if (this.singleItemLeft) {
        this.$analytics.events.viewedAllRecommendedItemsInWidget(params);
      }
      this.$router.push(item.route);
    },
  },
};
</script>

<style lang='scss' scoped>
.recommended-today-component-container {
  @include padded-widget;
}
</style>
